import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { WebPubSubClient } from '@azure/web-pubsub-client';

import { environment } from '@environments/environment';

import { 
  catchError, 
  Observable, 
  of, 
  Subject, 
  switchMap, 
  take, 
  throwError,
  retry
} from 'rxjs';

import { 
  connectionState, 
  PubsubEvent, 
  PubsubEventTypeEnum, 
  PubSubNegotiateRes
} from '../models';
import { Notification } from '@core/notifications/models';

@Injectable({
  providedIn: 'root'
})
export class PubsubService {
  readonly #http = inject(HttpClient);
  readonly #urlBase = `${environment.urlProfileManagement}/api/v1`;

  #client?: WebPubSubClient;
  #currentUserId?: number;
  #reconnectAttempts = 0;
  readonly #maxReconnectAttempts = 5;
  #reconnectTimeoutId?: ReturnType<typeof setTimeout>;

  readonly #connectionStateSignal = signal<connectionState>('Disconnected');
  readonly connectionState = this.#connectionStateSignal.asReadonly();

  readonly isConnected = computed(() => this.#connectionStateSignal() === 'Connected' && !!(this.#client));

  private readonly taskEventSubject = new Subject<Notification>();
  public readonly taskEvent$ = this.taskEventSubject.asObservable();

  private readonly notificationEventSubject = new Subject<Notification>();
  public readonly notificationEvent$ = this.notificationEventSubject.asObservable();

  /**
   * Negotiates with the server for a connection string.
   *
   * @param {string} userId - The ID of the user.
   * @return {Observable<string>} An observable that emits the connection string when 
   * the negotiation is successful, or throws an error if negotiation fails or the connection 
   * string is empty.
   */
  #negotiate(userId: number): Observable<string> {
    return this.#http.post<PubSubNegotiateRes>(
      `${this.#urlBase}/pubsub/negotiate`,
      { id: userId },
    )
    .pipe(
      retry(1),
      switchMap((res) => {
        return res.connectionString
            ? of(res.connectionString)
            : throwError(() => new Error('Empty connection string.'));
      }),
      catchError((err) => {
          console.error('Negotiation error:', err);
          return throwError(() => new Error('Negotiation failed.'));
      })
    );
  }

  /**
   * Establishes a connection with the PubSub service.
   *
   * @param {string} userId - The ID of the user.
   * @return {Observable<void>} An observable that emits void when the connection is
   * successfully established.
   */
  public connect(userId: number): Observable<void> {
    if (this.#client && this.#connectionStateSignal() === 'Connected') {
      return of(void 0);
    }

    this.#currentUserId = userId;
    this.#reconnectAttempts = 0;

    return this.#establishConnection(userId);
  }

  #establishConnection(userId: number): Observable<void> {
    return this.#negotiate(userId).pipe(
      take(1),
      switchMap(
        (url) =>
          new Observable<void>((subscriber) => {
              this.#connectionStateSignal.set('Connecting');
              this.#cleanup();
              
              this.#client = new WebPubSubClient(url);

              this.#client.on(
                'server-message',
                this.#handleServerMessage.bind(this),
              );

              this.#client.on('connected', () => {
                  this.#connectionStateSignal.set('Connected');
                  this.#reconnectAttempts = 0;
                  subscriber.next();
                  subscriber.complete();
              });

              this.#client.on('disconnected', () => {
                this.#connectionStateSignal.set('Disconnected');
                this.#handleDisconnection();
              });

              this.#client.start().catch((err) => {
                console.error('PubSub start failed:', err);
                this.#connectionStateSignal.set('Disconnected');
                subscriber.error(err);
              });
          }),
        ),
        catchError((error) => {
            console.error('Connection error:', error);
            return throwError(() => new Error('Connection failed.'));
        }),
    );
  }

  #handleDisconnection(): void {
    if (!this.#currentUserId || this.#reconnectAttempts >= this.#maxReconnectAttempts) {
      console.warn('Max reconnection attempts reached or no userId');
      return;
    }

    this.#reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.#reconnectAttempts - 1), 10000);

    console.log(`Reconnecting in ${delay}ms (attempt ${this.#reconnectAttempts}/${this.#maxReconnectAttempts})`);
    
    this.#connectionStateSignal.set('Reconnecting');
    
    this.#reconnectTimeoutId = setTimeout(() => {
      this.#establishConnection(this.#currentUserId!).subscribe({
        error: (err) => console.error('Reconnection failed:', err)
      });
    }, delay);
  }

  #cleanup(): void {
    if (this.#reconnectTimeoutId) {
      clearTimeout(this.#reconnectTimeoutId);
      this.#reconnectTimeoutId = undefined;
    }
    
    if (this.#client) {
      this.#client.stop();
      this.#client = undefined;
    }
  }

  /**
   * Handles a server message event and emits the parsed data to the event observable.
   *
   * @param {OnServerDataMessageArgs} event - The event containing the server message.
   * @return {void} This function does not return anything.
   */
  #handleServerMessage(event: any): void {
    let eventModel: {message: PubsubEvent<any>} = event;
    try {
      let parsed: any;
      let messageData: any;

      if (typeof eventModel === 'object' && 'message' in eventModel) {
        messageData = eventModel.message?.data;
        parsed = messageData?.data;
      } else {
        console.warn('Unknown message format:', eventModel);
        return;
      }

      const eventType = messageData?.type;
      
      switch (eventType as PubsubEventTypeEnum.NOTIFICATIONS | PubsubEventTypeEnum.TASKS) {
        case PubsubEventTypeEnum.TASKS:
          // this.taskEventSubject.next(parsed);
          break;
        case PubsubEventTypeEnum.NOTIFICATIONS:
          this.notificationEventSubject.next(parsed);
          break;
        default:
          console.warn('Unknown event type:', eventType);
      }
    } catch (error) {
      console.error('Failed to parse server message:', error, { event });
    }
  }

  /**
   * Disconnects the client from the Web PubSub service.
   *
   * @return {void} This function does not return anything.
   */
  public disconnect(): void {
    this.#currentUserId = undefined;
    this.#cleanup();
    this.#connectionStateSignal.set('Stopped');
  }
}
